import React, { useState } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import PaywallNotice from "@/components/PaywallNotice";
import HandwrittenUpload from "@/components/HandwrittenUpload";
import { useAuth } from "@/auth/AuthContext";
import { AIOutputDisclaimer } from "@/components/LegalDisclaimers";
import { 
  Sparkles, Target, TrendingUp, Award, CheckCircle2, 
  AlertCircle, Lightbulb, FileCheck, Lock, Camera, FileText
} from "lucide-react";

const EvaluationPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [marks, setMarks] = useState(10);
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [hitPaywall, setHitPaywall] = useState(false);
  const [inputMode, setInputMode] = useState("type"); // "type" or "handwritten"

  const handleEvaluate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setHitPaywall(false);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/eval/mains`, {
        question,
        answer,
        marks,
      });
      setEvaluation(data);
      toast({ description: "Evaluation generated." });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403 && msg) {
        setHitPaywall(true);
        toast({ variant: "destructive", description: msg });
      } else {
        toast({ variant: "destructive", description: msg || "Could not evaluate answer." });
      }
    } finally {
      setLoading(false);
    }
  };

  const isPro = user?.role === "PRO";

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6" data-testid="eval-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Sparkles className="w-8 h-8" />
              AI Evaluation Engine
            </h1>
            <p className="text-violet-100">
              Get examiner-style, structured feedback on your mains answers with AI-powered analysis
            </p>
          </div>
          <Badge className={`px-4 py-2 text-sm ${isPro ? 'bg-amber-500' : 'bg-white/20'}`}>
            {isPro ? '⭐ PRO Access' : '🔓 Upgrade Required'}
          </Badge>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <FileCheck className="w-5 h-5 text-blue-600" />
              <CheckCircle2 className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-xs font-semibold text-blue-900">Structure Audit</p>
            <p className="text-[11px] text-blue-700">Flow & organization check</p>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <Target className="w-5 h-5 text-emerald-600" />
              <TrendingUp className="w-4 h-4 text-emerald-600" />
            </div>
            <p className="text-xs font-semibold text-emerald-900">Depth Analysis</p>
            <p className="text-[11px] text-emerald-700">Coverage & dimensions</p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <Lightbulb className="w-5 h-5 text-purple-600" />
              <Sparkles className="w-4 h-4 text-purple-600" />
            </div>
            <p className="text-xs font-semibold text-purple-900">Keyword Enhancement</p>
            <p className="text-[11px] text-purple-700">Missing terms & data</p>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <Award className="w-5 h-5 text-amber-600" />
              <span className="text-xs font-bold text-amber-900">0-10</span>
            </div>
            <p className="text-xs font-semibold text-amber-900">Score Estimate</p>
            <p className="text-[11px] text-amber-700">UPSC-style marking</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        <Card className="border-slate-200 bg-white shadow-sm" data-testid="eval-form-card">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="bg-violet-100 p-2 rounded-lg">
                <FileCheck className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Submit Your Answer</h2>
                <p className="text-xs text-slate-600">Paste your question and answer for detailed evaluation</p>
              </div>
            </div>

            <form className="space-y-4" onSubmit={handleEvaluate} data-testid="eval-form">
              <div className="space-y-2">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">
                  QUESTION
                </p>
                <Textarea
                  id="eval-question"
                  rows={3}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Paste the exact mains question you attempted..."
                  required
                  className="text-sm resize-none"
                  data-testid="eval-question-input"
                />
              </div>

              {/* Input Mode Toggle */}
              <div className="space-y-2">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">
                  ANSWER INPUT METHOD
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={inputMode === "type" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setInputMode("type")}
                    className={inputMode === "type" ? "bg-violet-500 hover:bg-violet-600" : ""}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Type Answer
                  </Button>
                  <Button
                    type="button"
                    variant={inputMode === "handwritten" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setInputMode("handwritten")}
                    className={inputMode === "handwritten" ? "bg-indigo-500 hover:bg-indigo-600" : ""}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Upload Handwritten
                  </Button>
                </div>
              </div>

              {inputMode === "type" ? (
                <>
                  <div className="space-y-2">
                    <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">
                      YOUR ANSWER
                    </p>
                    <Textarea
                      id="eval-answer"
                      rows={10}
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder="Paste or type your full answer here..."
                      required
                      className="text-sm resize-none"
                      data-testid="eval-answer-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">
                      QUESTION MARKS
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[10, 15, 20].map((m) => (
                        <Button
                          key={m}
                          type="button"
                          variant={marks === m ? "default" : "outline"}
                          size="sm"
                          onClick={() => setMarks(m)}
                          className={marks === m ? "bg-violet-500 hover:bg-violet-600" : ""}
                        >
                          {m} marks
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shadow-lg"
                    disabled={loading}
                    data-testid="eval-submit-button"
                  >
                    {loading ? (
                      <>
                        <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                        Evaluating...
                      </>
                    ) : (
                      <>
                        <Target className="w-4 h-4 mr-2" />
                        Evaluate My Answer
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">
                      QUESTION MARKS
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[10, 15, 20].map((m) => (
                        <Button
                          key={m}
                          type="button"
                          variant={marks === m ? "default" : "outline"}
                          size="sm"
                          onClick={() => setMarks(m)}
                          className={marks === m ? "bg-violet-500 hover:bg-violet-600" : ""}
                        >
                          {m} marks
                        </Button>
                      ))}
                    </div>
                  </div>

                  <HandwrittenUpload
                    marks={marks}
                    suite="GS2"
                    onEvaluationComplete={(data) => {
                      setEvaluation(data);
                      if (data.extracted_question) {
                        setQuestion(data.extracted_question);
                      }
                      if (data.extracted_answer) {
                        setAnswer(data.extracted_answer);
                      }
                      toast({ description: "✅ Handwritten answer evaluated!" });
                    }}
                    onExtractedData={(data) => {
                      if (data.question) setQuestion(data.question);
                      if (data.answer) setAnswer(data.answer);
                    }}
                  />
                </>
              )}

              {!isPro && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <Lock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-900">
                    <strong>PRO Feature:</strong> Get structure audit, depth analysis, keyword suggestions, and score estimation.
                  </p>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        <EvaluationViewer evaluation={evaluation} hitPaywall={hitPaywall} marks={marks} />
      </div>
    </div>
  );
};

const EvaluationViewer = ({ evaluation, hitPaywall, marks = 10 }) => {
  if (hitPaywall) {
    return (
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
        <CardContent className="p-6">
          <PaywallNotice
            title="AI Evaluation is PRO-only"
            description="Upgrade to CLARIAS PRO to unlock AI evaluation with structure audit, depth analysis, keyword enhancement, missing dimensions, better intro/conclusion and score estimation."
            testIdPrefix="eval-paywall"
          />
        </CardContent>
      </Card>
    );
  }

  if (!evaluation) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50/50" data-testid="eval-empty-card">
        <CardContent className="p-8 flex flex-col justify-center items-center h-full text-center space-y-4">
          <div className="bg-gradient-to-br from-violet-100 to-fuchsia-100 p-4 rounded-full">
            <Target className="w-8 h-8 text-violet-600" />
          </div>
          <div className="space-y-2">
            <p className="text-base font-bold text-slate-900">Your Evaluation Will Appear Here</p>
            <p className="text-sm text-slate-600 max-w-md">
              Submit your answer to receive detailed AI-powered feedback from a UPSC examiner&apos;s perspective
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 w-full max-w-sm text-left">
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mb-1" />
              <p className="text-[11px] font-semibold text-slate-900">Structure Check</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <TrendingUp className="w-4 h-4 text-blue-600 mb-1" />
              <p className="text-[11px] font-semibold text-slate-900">Depth Analysis</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <Lightbulb className="w-4 h-4 text-amber-600 mb-1" />
              <p className="text-[11px] font-semibold text-slate-900">Keywords</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <Award className="w-4 h-4 text-purple-600 mb-1" />
              <p className="text-[11px] font-semibold text-slate-900">Score (0-10)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 bg-white shadow-sm" data-testid="eval-result-card">
      <CardContent className="p-6 space-y-5">
        {/* Header with Score */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-violet-100 to-fuchsia-100 p-2 rounded-lg">
              <Award className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Evaluation Results</h2>
              <p className="text-xs text-slate-600">AI-powered examiner feedback</p>
            </div>
          </div>
          {typeof evaluation.score_estimate === "number" && (
            <div className="text-center">
              <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white px-4 py-2 rounded-xl">
                <p className="text-xs font-semibold">Estimated Score</p>
                <p className="text-2xl font-bold">{evaluation.score_estimate}/{marks}</p>
              </div>
            </div>
          )}
        </div>

        {/* Structure Feedback */}
        {evaluation.structure_feedback?.length > 0 && (
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="eval-structure-section">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-blue-600" />
                <p className="text-sm font-bold text-blue-900">Structure & Flow</p>
              </div>
              <ul className="space-y-1.5">
                {evaluation.structure_feedback.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <CheckCircle2 className="w-3 h-3 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>{p.comment}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Depth Feedback */}
        {evaluation.depth_feedback?.length > 0 && (
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="eval-depth-section">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-600" />
                <p className="text-sm font-bold text-emerald-900">Depth & Coverage</p>
              </div>
              <ul className="space-y-1.5">
                {evaluation.depth_feedback.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <TrendingUp className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>{p.comment}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Keywords */}
        {evaluation.keyword_suggestions?.length > 0 && (
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50" data-testid="eval-keyword-section">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-purple-600" />
                <p className="text-sm font-bold text-purple-900">Keywords to Add</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {evaluation.keyword_suggestions.map((keyword, i) => (
                  <Badge key={i} className="bg-purple-100 text-purple-900 text-xs border-purple-300">
                    {keyword}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Missing Examples */}
        {evaluation.missing_examples?.length > 0 && (
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="eval-missing-examples-section">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <p className="text-sm font-bold text-amber-900">Missing Data & Examples</p>
              </div>
              <ul className="space-y-1.5">
                {evaluation.missing_examples.map((example, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <span className="text-amber-600 flex-shrink-0">•</span>
                    <span>{example}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Better Intro */}
        {evaluation.better_intro && (
          <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50" data-testid="eval-better-intro-section">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                <p className="text-sm font-bold text-indigo-900">Suggested Better Introduction</p>
              </div>
              <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-indigo-200">
                {evaluation.better_intro}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Better Conclusion */}
        {evaluation.better_conclusion && (
          <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50" data-testid="eval-better-conclusion-section">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                <p className="text-sm font-bold text-indigo-900">Suggested Better Conclusion</p>
              </div>
              <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-indigo-200">
                {evaluation.better_conclusion}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Improved Answer */}
        {evaluation.improved_answer && (
          <Card className="border-slate-200 bg-white" data-testid="eval-improved-answer-section">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-slate-600" />
                <p className="text-sm font-bold text-slate-900">Improved Model Answer</p>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 max-h-96 overflow-y-auto text-xs text-slate-700 space-y-2">
                {evaluation.improved_answer.split("\n").map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Meta Tips */}
        {evaluation.meta_tips?.length > 0 && (
          <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50" data-testid="eval-meta-tips-section">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-rose-600" />
                <p className="text-sm font-bold text-rose-900">Meta Tips for Improvement</p>
              </div>
              <ul className="space-y-1.5">
                {evaluation.meta_tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <span className="text-rose-600 flex-shrink-0 font-bold">→</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* AI Output Disclaimer - MANDATORY on answer evaluation screens */}
        <AIOutputDisclaimer variant="small" />
      </CardContent>
    </Card>
  );
};

export default EvaluationPage;
